import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  saveExecSummary,
  generateExecSummary,
  DEFAULT_META,
} from '../data/metaApi.js'
import { useHistoryStore } from '../store/historyStore.js'
import {
  rememberExecSummary,
  clearExecSummaryOverride,
} from '../data/execSummaryOverride.js'
import { withArchiveGuard } from './_archiveGuard.js'

/**
 * useSaveExecSummary — save (or clear) the execSummary slot on meta via
 * the server-side partial update. Optimistic cache write; rolls back on
 * error.
 *
 * Usage:
 *   const save = useSaveExecSummary()
 *   save.mutate({ tldr, flagsSummary, generatedAt, generatedBy, edited: true })
 *   save.mutate({ clear: true })   // wipes the summary
 */
export function useSaveExecSummary() {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (payload) => {
      const execSummary = await saveExecSummary(payload)
      return execSummary
    },

    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: ['meta'] })
      const previous = queryClient.getQueryData(['meta']) || DEFAULT_META

      // Drop any prior shield so the optimistic value is the visible state
      // until onSuccess re-establishes the override against the server's
      // returned exec summary.
      clearExecSummaryOverride()

      useHistoryStore
        .getState()
        .pushSnapshot(
          'Exec summary',
          queryClient.getQueryData(['goals']),
          previous
        )

      const nextExec = payload?.clear ? null : payload
      queryClient.setQueryData(['meta'], {
        ...previous,
        execSummary: nextExec,
      })
      return { previous }
    },

    onSuccess: (execSummary) => {
      const current = queryClient.getQueryData(['meta']) || DEFAULT_META
      queryClient.setQueryData(['meta'], { ...current, execSummary })
      // Shield against the meta query's 60-second refetch landing on
      // stale KV data and silently reverting this save. useMetaData
      // checks getExecSummaryOverride and prefers it over the refetched
      // value during the TTL.
      rememberExecSummary(execSummary ?? null)
    },

    onError: (err, vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['meta'], ctx.previous)
    },
  })
  return withArchiveGuard(mutation)
}

/**
 * useGenerateExecSummary — POST to /ai-summary and receive a fresh
 * { tldr, winBullets, flagBullets, winsSummary, flagsSummary } payload.
 * Does NOT auto-save; the component decides whether to call
 * useSaveExecSummary after the user reviews.
 */
export function useGenerateExecSummary() {
  const mutation = useMutation({
    mutationFn: async (args) => generateExecSummary(args || {}),
  })
  return withArchiveGuard(mutation)
}
